// The five categories the AI cost report is organised into — Omar's list, in
// Omar's order, with one home.
//
// ★ WHY THIS IS IN `@dynamo/schema` AND NOT IN EITHER APP.
//
// The gateway CLASSIFIES a recorded request into one of these; the Console
// DISPLAYS the same five in the same order, twice (once for the company, once
// for each course). Those are two halves of one rule, and they were about to be
// two lists in two repositories that agreed only for as long as somebody
// remembered to edit both (`feedback_one_rule_one_place`). Both packages already
// depend on this one, so the list lives here and neither owns it.
//
// ── Omar, 2026-09-08 (verbatim) ─────────────────────────────────────────────
//
//   "Organise costs into five clearly distinguishable categories:
//      · Question and text generation.
//      · Subtitle generation.
//      · Image generation.
//      · Video generation.
//      · Translation.
//    … Both views must display the same five categories, in this order …
//    Video should show 'Not available'; this does not add video generation to
//    the development scope."
//
// ── THE ORDER IS THE FEATURE, and it is not a sort ──────────────────────────
//
// Sorted by cost, "Translation" is row 5 for one course and row 2 for the next,
// and the company overview matches neither — so every read starts by finding the
// rows again. A FIXED order makes opening a course a substitution of numbers
// into a shape already learned, which is the whole reason for having five
// categories rather than a list. Do not sort this array anywhere.
//
// ── ★ VIDEO HAS NO BUCKET, DELIBERATELY ─────────────────────────────────────
//
// Video generation does not exist in this product. It is a row on a report, not
// a thing that can be recorded. So it is in `AI_COST_CATEGORIES` (it must appear
// on screen) and NOT in `AiCostBucket` (nothing can ever be classified into it).
// The type system therefore refuses to produce a video group from data, and the
// Console renders the row from the category list alone.
//
// The day video generation ships, this becomes two edits: `availability` flips
// to `"reported"` here, and `operationBucket()` in the gateway gains one branch.
export const AI_COST_CATEGORIES = Object.freeze([
    {
        key: "questions",
        // ── ★ RENAMED 2026-09-18 (Omar), AND THE SIXTH CATEGORY WITHDRAWN ───────
        //
        // His instruction, verbatim: *"I have decided to simplify OpenAI cost
        // reporting. This supersedes my earlier request for a separate 'Course
        // generation' cost item. … Remove the separate OpenAI — Course generation
        // entry. Keep a single OpenAI entry. Change its description from 'Question
        // and text generation' to 'Question, text and course generation'. … No
        // proportional split between course generation and question/text generation
        // is needed."*
        //
        // ★ WHY THIS IS THE BETTER SHAPE, not merely the requested one. A separate
        //   row had to answer "what did course generation cost?" with a sentence
        //   rather than a figure, because OpenAI bills a PROJECT per company and
        //   itemises by MODEL, not by operation — and both features use the same
        //   model in the same project. One row makes the screen agree with the
        //   invoice exactly: what the provider reports as one amount is displayed
        //   as one amount, and `categoryForProviderCost` mapping every OpenAI
        //   charge here is now simply TRUE rather than a limitation to explain.
        //
        // ⚠️ NOTHING WAS LOST TO ACHIEVE IT. `ai_usage` still records
        //    `course.outline` and `course.build` as distinct operations with their
        //    own tokens, model and duration; only the REPORT groups them. The
        //    bucket is computed at READ time, so this regrouped every historical
        //    row on deploy — no migration, and reversing it would be as cheap.
        label: "Question, text and course generation",
        availability: "reported",
        costReporting: "per_client",
        billedBy: { key: "openai", label: "OpenAI" },
    },
    {
        key: "transcription",
        label: "Subtitle generation",
        availability: "reported",
        costReporting: "per_client",
        billedBy: { key: "deepgram", label: "Deepgram" },
    },
    {
        key: "images",
        label: "Image generation",
        availability: "reported",
        // Corrected 2026-09-11 — see `costReporting`'s note. Vertex AI carries the
        // client label; the charge arrives labelled.
        costReporting: "per_client",
        billedBy: { key: "google", label: "Google" },
    },
    {
        key: "video",
        label: "Video generation",
        availability: "not_built",
        costReporting: "not_built",
        // Not built, and no provider chosen. See `billedBy`'s note.
        billedBy: null,
    },
    {
        key: "translation",
        label: "Translation",
        availability: "reported",
        // Corrected 2026-09-11 — Cloud Translation v3 carries the client label.
        costReporting: "per_client",
        billedBy: { key: "google", label: "Google" },
    },
]);
/**
 * The sixth row, rendered only when something landed in it.
 *
 * Its description is the whole point: it explains why an unexpected row appeared
 * without implying that anything is broken.
 */
export const OTHER_AI_COST_CATEGORY = Object.freeze({
    key: "other",
    label: "Other AI usage",
    // Whatever landed here is by definition unrecognised, so its provider is not
    // knowable from the category (`feedback_an_empty_list_may_mean_everything`).
    billedBy: null,
    description: "AI work that does not yet belong to one of the categories above. " +
        "It is counted here so nothing is left out of the total.",
    availability: "reported",
    // Unrecognised work: we cannot say which provider, so we cannot say whether
    // that provider reports per client.
    costReporting: "not_per_client",
});
/** Label for any bucket, including `other`. Used where only a key is at hand. */
export function aiCostBucketLabel(bucket) {
    if (bucket === "other")
        return OTHER_AI_COST_CATEGORY.label;
    const found = AI_COST_CATEGORIES.find((c) => c.key === bucket);
    // Unreachable while `AiCostBucket` and the array agree — which the package's
    // own test asserts, so this is a type-level formality rather than a fallback
    // that could quietly hide a missing category.
    return found ? found.label : OTHER_AI_COST_CATEGORY.label;
}
/**
 * Charges we KNOW are Dynamo's own testing, keyed by the marker the provider
 * reported them under.
 *
 * ★ WHY. Omar, 2026-09-11: *"Record the spending-limit test charges as Dynamo
 *   internal testing, once their origin is confirmed. Keep them separate from
 *   client costs and retain them in the overall reconciliation."*
 *
 *   On 2026-09-10 a spending limit was proved to be enforced by deliberately
 *   burning through a $0.01 cap in a throwaway OpenAI project. That cost about
 *   three cents and belongs to no client, so it shows in the Console as an
 *   unmatched charge — truthful, and indistinguishable from an attribution
 *   FAILURE, which is the thing that list exists to surface.
 *
 * ⚠️ WHY THIS IS A CONSTANT AND NOT A ROW IN `ai_provider_account`. That table's
 *   unique key is `(provider, org_id)` and provisioning claims its row with
 *   `on conflict … do nothing`, then carries `external_id` forward through
 *   `coalesce`. Pointing an internal workspace's row at this archived probe
 *   project to borrow its label would therefore make that workspace
 *   unprovisionable for ever — a row added for REPORTING would silently become
 *   a GATE (`feedback_an_entry_in_a_capability_table_is_a_gate_that_spends`).
 *   The label is a fact about one historical probe, not tenant data, so it lives
 *   in code where it cannot reach the provisioning path at all.
 *
 *   If naming internal spend ever becomes routine rather than a one-off, the
 *   answer is a small table of its OWN read only by the reporting path — not
 *   this constant grown long, and never `ai_provider_account`.
 */
export const INTERNAL_ATTRIBUTION_LABELS = Object.freeze({
    // `dynamo-spendprobe-1789046792`, archived. Confirmed against OpenAI's own
    // project record on 2026-09-11, with a fabricated project id as a control
    // so the check could tell presence from absence.
    proj_H7aCtYunjMBntGRDMISj6Xbi: "Dynamo internal testing — OpenAI spending-limit test, 10 Sept 2026",
});
/**
 * The label for an unmatched charge, or `null` when it is genuinely unexplained.
 *
 * `null` is the important half: everything not named above is a real attribution
 * gap and must keep reading as one.
 */
export function internalAttributionLabel(attributionKey) {
    if (attributionKey === null)
        return null;
    return INTERNAL_ATTRIBUTION_LABELS[attributionKey] ?? null;
}
//# sourceMappingURL=ai-cost-categories.js.map